import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, InputBase, makeStyles, TextField } from '@material-ui/core';
import _, { isFunction } from 'lodash';
import { useSelector } from 'react-redux';
import DiaLog from '../../../../components/Dialog';
import { i18n } from '../../../../language';
import { DropDownIcon, DropUpIcon, ScanIcon } from '../../../../components/Icon';
import { useScanPage } from '../../../../utils/useScanQrcode';
import EquipmentAsset from '../../../../components/EquipmentAsset';
import { equipmentAPI, systemAPI } from '../../../../apis';
import AutoCompleteInput from '../../../../components/AutoCompleteInput';
import { EQUIPMENT_LENGTH, REMARKS_FIELD } from '../../../../comm/constant';
import useBarcodeListener from '../../../../utils/useBarcodeListener';
import useMobile from '../../../../utils/useMobile';
import StyleButton from '../../../../components/StyleButton';
import {
  DWP_COMM_REMARK_BMS_ISFA,
  DWP_COMM_REMARK_BMS_L,
  DWP_COMM_REMARK_BMS_M,
  DWP_COMM_REMARK_BMS_S,
} from '../../../../comm/commandQRcode';
import SingleADSDialog from '../ADS/SingleADSDialog';
import { getUUID, useGetUserInfo } from '../../../../utils';
import RedStar from '../../../../components/RedStar';
import { getNewRequestProviderOptions, renderProviderGroup } from '../Util';
import AddRemarks from '../../../../components/AddRemarks';
import useGetRemarksListAPI from '../../hooks/useGetRemarksListAPI';
import { handleScanHkid, handleScanPatientQrCode } from '.';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#7d86a9',
  },
  wardAutoSelect: {
    marginTop: '1rem',
  },
  inputBase: ({ error }) => ({
    border: error ? 'solid 1px #f44336' : 'solid 1px #e4e7eb',
    borderRadius: '10px',
    height: '3.5rem',
    width: '24.5rem',
    padding: '0 1.25rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  }),
  errorMsg: {
    color: '#f44336',
    margin: '0.5rem 0.5rem 0',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  otherInfoBtn: {
    cursor: 'pointer',
  },
  dropDownButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '1.5rem',
    height: '1.5rem',
    marginRight: '1.25rem',
  },
  dropDownIcon: {
    fontSize: '0.938rem',
    color: '#3e3e3e',
  },
  otherInformationTitle: {
    fontSize: '1.125rem',
    color: '#3e3e3e',
  },
  scanButton: {
    marginBottom: '0.375rem',
    flexBasis: '66%',
    marginLeft: '1.5rem',
    marginTop: '1rem',
  },
  scanIcon: {
    color: '#538fff',
  },
  star: {
    color: '#ff0300',
  },
  textField: {
    borderRadius: '0.625rem',
    border: 'solid 1px #e4e7eb',
    padding: '0 1rem',
    '& .MuiInputBase-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
      lineHeight: '1.8rem',
    },
    '& :before': {
      content: 'none',
    },
    '& :after': {
      content: 'none',
    },
  },
}));

const TitleInput = ({
  title,
  placeholder,
  flexBasis,
  value,
  onChange,
  endAdornment,
  enterHandler,
  onFocus,
  onBlur,
  length = 'unset',
  error,
  errorMsg,
  required = false,
}) => {
  const classes = useStyles({ error });

  const inputHandler = (event) => {
    if (isFunction(onChange)) {
      onChange(event.target.value);
    }
  };

  const onKeyDownHandler = (event) => {
    if ((event.code === 'Enter' || event.code === 'NumpadEnter') && isFunction(enterHandler)) {
      enterHandler(event.target.value);
    }
  };

  return (
    <Box display="flex" flexDirection="column" flexBasis={flexBasis}>
      <div className={classes.title}>
        {title}
        {required && <RedStar />}
      </div>
      <InputBase
        className={classes.inputBase}
        placeholder={placeholder}
        style={{ width: '100%', marginTop: '1.063rem' }}
        onChange={inputHandler}
        value={value}
        endAdornment={endAdornment}
        onKeyDown={onKeyDownHandler}
        onFocus={onFocus}
        onBlur={onBlur}
        inputProps={{ maxLength: length }}
      />
      {error && <div className={classes.errorMsg}>{i18n.t(errorMsg)}</div>}
    </Box>
  );
};

const replaceStrBmss = DWP_COMM_REMARK_BMS_S;
const replaceStrBmsm = DWP_COMM_REMARK_BMS_M;
const replaceStrBmsl = DWP_COMM_REMARK_BMS_L;
const replaceStrBmsIsfa = DWP_COMM_REMARK_BMS_ISFA;

const replaceHandler = (oldReplaceValue, newReplaceValue, value, enter, temp) => {
  const reg = new RegExp(oldReplaceValue, 'g');
  let str;
  if (value.target) {
    str = value.target.value.replace(reg, newReplaceValue);
  } else if (enter) {
    str = `${temp.remarks + value.replace(reg, newReplaceValue)}\n`;
  } else {
    str = `${temp.remarks + value.replace(reg, newReplaceValue)}`;
  }

  return str;
};

const judgeConstantRemark = (val, constantRemark) => val.indexOf(constantRemark) !== -1;

const handleInputRemark = ({ value, setFormData, enter }) => {
  setFormData((preData) => {
    let temp = { ...preData };
    let val = value.target ? value.target.value : value;

    if (judgeConstantRemark(val, replaceStrBmss)) {
      val = replaceHandler(replaceStrBmss, REMARKS_FIELD.BIPAP_MASK_SIZE_S, value, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsm)) {
      val = replaceHandler(replaceStrBmsm, REMARKS_FIELD.BIPAP_MASK_SIZE_M, value, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsl)) {
      val = replaceHandler(replaceStrBmsl, REMARKS_FIELD.BIPAP_MASK_SIZE_L, value, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsIsfa)) {
      val = replaceHandler(replaceStrBmsIsfa, REMARKS_FIELD.EXCEPT_ISOLATION_WARD, value, enter, temp);
    } else if (val.indexOf(REMARKS_FIELD.START_CODE) !== -1 && val.indexOf(REMARKS_FIELD.END_CODE) !== -1) {
      if (enter) {
        val = temp.remarks + val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => `${value.slice(13, -12)}\n`);
      } else {
        val = value.target
          ? val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => value.slice(13, -12))
          : temp.remarks + val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => value.slice(13, -12));
      }
    }

    temp.remarks = val;
    return { ...temp };
  });
};

const emptyEquipment = {
  category: '',
  categoryName: '',
  availableQty: 0,
  assets: [],
  loanQty: 1,
};

const handleRemoveCategory =
  ({ setFormData, setEquipmentAssets }) =>
  (index) => {
    if (index === 0) {
      setFormData((preFormData) => ({ ...preFormData, provider: '' }));
      setEquipmentAssets((preData) => (preData.length === 1 ? [emptyEquipment] : [...preData.slice(1)]));
    } else {
      setEquipmentAssets((preData) => [...preData.slice(0, index), ...preData.slice(index + 1)]);
    }
  };

const handleChangeCategory =
  ({ setEquipmentAssets, setFormData, providerList, formData, flag }) =>
  (value, categoryName, wardIds) =>
    setEquipmentAssets((preData) => {
      let tempData = [...preData];
      setFormData((preFormData) => {
        if (preFormData.provider) {
          return preFormData;
        } else {
          let providers = _.filter(providerList, (item) => item.value !== formData.ward);

          let filterProvider = _.filter(providers, (item) => _.some(wardIds, (wardItem) => item.wardId === wardItem));

          let provider = filterProvider?.find(
            (item) => item.categorys.find((categoryItem) => categoryItem.id === value).mainStore === 1
          );

          return { ...preFormData, provider: provider ? provider.wardId : wardIds?.[0] };
        }
      });

      tempData.splice(flag, 1, {
        ...preData[flag],
        category: value,
        categoryName,
      });
      return tempData;
    });

const handleAddAsset =
  ({ setEquipmentAssets }) =>
  ({ categoryId, model, assetId, localCode, categoryName, remark }) =>
    setEquipmentAssets((preData) => {
      let tempData = [...preData];
      let newAsset = {
        assetId,
        localCode,
        model,
        remark,
      };

      // 已存在對應的category
      if (_.some(tempData, (item) => item.category === categoryId)) {
        let index = _.findIndex(tempData, (item) => item.category === categoryId);

        // 存在對應的 assetId
        if (_.some(tempData[index].assets, (item) => item.assetId === assetId)) {
          return tempData;
        } else {
          // 不存在對應的 assetId
          let loanQty = tempData[index].loanQty;
          let assets = tempData[index].assets;
          if (assets.length < loanQty) {
            tempData[index].assets.push(newAsset);
          }
        }
      }
      // 不存在對應的category，但是有一個 category tab 為空
      else if (_.some(tempData, (item) => item.category === '')) {
        let emptyIndex = tempData.length - 1;
        tempData[emptyIndex].category = categoryId;
        tempData[emptyIndex].assets.push(newAsset);
      }
      // 不存在對應的category，並且沒有 category tab 為空
      else {
        let newEquipment = { ...emptyEquipment, category: categoryId, categoryName, assets: [newAsset] };
        tempData.push(newEquipment);
      }

      return tempData;
    });

const handleRemoveAsset =
  ({ setEquipmentAssets, flag }) =>
  (id) =>
    setEquipmentAssets((preData) => {
      let tempData = [...preData];
      tempData[flag].assets = _.filter(tempData[flag].assets, (item) => item.assetId !== id);
      return tempData;
    });

const NewLoanRequestModelBDialog = ({ dialogCloseHandler, dialogFinishHandler }) => {
  const initFormData = {
    ward: '',
    provider: '',
    patient: '',
    bedID: '',
    remarks: '',
  };
  const classes = useStyles();
  const [formData, setFormData] = useState(initFormData);
  const [otherInformationOpen, setOtherInformationOpen] = useState(false);
  const loginInfo = useSelector((state) => state.loginInfo);
  const [providerList, setProviderList] = useState();
  const [wardList, setWardList] = useState({});
  const [equipmentAssets, setEquipmentAssets] = useState([emptyEquipment]);
  const [flag, setFlag] = useState(0);
  const [barcodeOpen, setBarcodeOpen] = useState(true);
  const [errors] = useState({ patient: '' });
  const [isPad] = useMobile();
  const inputRef = useRef(null);
  const [adsPopup, setAdsPopup] = useState(false);
  const userInfo = useGetUserInfo();
  const adsRef = useRef(null);
  const [loanUniqueKey] = useState(getUUID);
  const [selectedCategoryWards, setSelectedCategoryWards] = useState([]);

  const requiredPatientHN = useMemo(() => {
    return _.some(selectedCategoryWards, (item) => String(item?.isPatientNo) === '1');
  }, [selectedCategoryWards]);

  useBarcodeListener((e) => {
    if (e.replace(/\s*/g, '').startsWith('HN')) {
      formChangeHandler('patient')(e.replace(/\s*/g, ''));
    } else if (
      e.indexOf(replaceStrBmss) !== -1 ||
      e.indexOf(replaceStrBmsm) !== -1 ||
      e.indexOf(replaceStrBmsl) !== -1 ||
      e.indexOf(replaceStrBmsIsfa) !== -1 ||
      (e.indexOf(REMARKS_FIELD.START_CODE) !== -1 && e.indexOf(REMARKS_FIELD.END_CODE) !== -1)
    ) {
      setOtherInformationOpen(true);
      inputRef.current.focus();
      formChangeHandler('remarks')(e);
    } else if (e.toUpperCase().startsWith('WB ')) {
      handleScanHkid(formChangeHandler('patient'), formChangeHandler('bedID'), e);
      setOtherInformationOpen(true);
    }
  }, barcodeOpen);

  const otherInformationController = () => setOtherInformationOpen((open) => !open);

  // barcode 開關
  const barcodeControl = useCallback(
    (props) => () => {
      setBarcodeOpen(props);
    },
    []
  );

  // 處理 assets 相關操作
  const equipmentAssetsHandler = useCallback(
    ({ type, flag }) => {
      switch (type) {
        case 'addEquipment':
          setEquipmentAssets((preData) => {
            return [...preData, emptyEquipment];
          });
          return;
        case 'changeCategory':
          return handleChangeCategory({ setEquipmentAssets, setFormData, providerList, formData, flag });
        case 'removeCategory':
          return handleRemoveCategory({ setFormData, setEquipmentAssets });
        case 'addAsset':
          return handleAddAsset({ setEquipmentAssets });
        case 'removeAsset':
          return handleRemoveAsset({ setEquipmentAssets, flag });
        case 'updateAvailableQty':
          return (newAvailableQty) =>
            setEquipmentAssets((preData) => {
              let tempData = [...preData];

              tempData.splice(flag, 1, {
                ...preData[flag],
                availableQty: newAvailableQty,
              });
              return tempData;
            });
        case 'updateLoanQty':
          return (newLoanQty) =>
            setEquipmentAssets((preData) => {
              let tempData = [...preData];
              let assets = tempData[flag].assets;

              if (newLoanQty < assets.length) {
                return tempData;
              }

              tempData.splice(flag, 1, {
                ...preData[flag],
                loanQty: newLoanQty,
              });
              return tempData;
            });
        default:
          return;
      }
    },
    [providerList, formData.ward]
  );

  // 處理 assets 以外的輸入
  const formChangeHandler = useCallback((key, enter) => {
    return (value) => {
      if (key === 'remarks') {
        handleInputRemark({ value, setFormData, enter });
        return;
      }
      if (key === 'patient') {
        setFormData((preData) => {
          let newValue = value;
          newValue = newValue.toUpperCase();
          newValue = newValue.replace(/[\(\)]+/g, '');
          return { ...preData, [key]: newValue };
        });
      } else {
        setFormData((preData) => ({ ...preData, [key]: value }));
      }
    };
  }, []);

  // 獲取 provider list
  const getProvider = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getProvider({ hospitalId: loginInfo.hospitalId, wardId: loginInfo.wardId });
      setProviderList(result.provides);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  // 獲取 ward list
  const getWardList = useCallback(async () => {
    try {
      const { result } = await systemAPI.getSysHospitalWardList({ hospitalId: loginInfo.hospitalId, wardId: null });
      setWardList(result?.[0]);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  useEffect(() => {
    (async () => {
      await getProvider();
    })();
    (async () => {
      await getWardList();
    })();
  }, []);

  // ward options
  const wardsOptions = useMemo(() => {
    let wards = [];

    if (Array.isArray(wardList?.wards)) {
      wards = _.map(wardList?.wards || [], (item) => {
        const showWardOption = item?.enableYn === 0;
        let roomOptions = [];
        const wardOption = {
          text: `${wardList.hospitalCode} - ${item.wardName}`,
          value: item.wardId,
          tagId: item.wardId,
          wardId: item.wardId,
          adsWardLocationId: null,
        };

        const hasRoomType = _.some(item.wardLocation, (locationItem) => locationItem.adsRoomType === 'R');
        if (hasRoomType) {
          roomOptions = _.filter(item.wardLocation, (locationItem) => locationItem.adsRoomType === 'R').map(
            (locationItem) => ({
              text: `${wardList.hospitalCode} - ${item.wardName} - ${locationItem.roomName}`,
              value: locationItem.adsWardLocationId,
              tagId: locationItem.adsWardLocationId,
              wardId: item.wardId,
              adsWardLocationId: locationItem.adsWardLocationId,
            })
          );
        }
        return showWardOption ? [wardOption, ...roomOptions] : [...roomOptions];
      }).flat();
    }

    // 过滤只要当前ward
    wards = _.filter(wards, (item) => item.wardId === loginInfo.wardId);

    if (wards.length > 0) {
      if (_.some(wards, (item) => item.value === loginInfo.wardId)) {
        formChangeHandler('ward')(loginInfo.wardId);
      } else {
        formChangeHandler('ward')(wards[0].value);
      }
    }

    return wards;
  }, [wardList, formChangeHandler]);

  // provider options
  const providersOptions = useMemo(() => {
    const params = {
      type: 'borrow',
      equipmentAssets,
      flag,
      providerList,
      currentWardId: '',
    };
    return getNewRequestProviderOptions(params);
  }, [providerList, formChangeHandler, formData.ward, equipmentAssets, getNewRequestProviderOptions, flag]);

  // 当前选择的wardId
  const wardId = wardsOptions.find((item) => item.value === formData.ward)?.wardId;
  // 当前选择的 adsWardLocationId
  const adsWardLocationId = wardsOptions.find((item) => item.value === formData.ward)?.adsWardLocationId;
  // 当前选择的 providerWardId
  const provideWardId = providersOptions.find((item) => item.value === formData.provider)?.wardId;
  // 当前选择的provider
  const currentProvider = useMemo(
    () => _.find(providersOptions, (item) => item?.value === formData?.provider),
    [formData, providersOptions]
  );
  // 当前选择的 categorySatelliteStoreId
  const categorySatelliteStoresId = useMemo(() => {
    const selectedCategory = equipmentAssets?.[0]?.category;
    if (!currentProvider || currentProvider?.isFloor) {
      // 如果选的是楼层 categorySatelliteStoresId 为null
      return null;
    } else {
      const availableCategory = currentProvider?.categorys;
      const currentStore = _.find(availableCategory, (item) => item?.id === selectedCategory)?.satelliteStores ?? [];
      return currentStore?.[0]?.categorySatelliteStoresId ?? null;
    }
  }, [equipmentAssets, currentProvider]);

  let submitLock = false;
  // new loan request 的 API
  const newLoanRequestAPI = useCallback(
    async ({ adsCategoryId, isAdsServer }) => {
      try {
        if (submitLock) {
          return;
        }
        submitLock = true;
        let params = {
          bedNo: formData.bedID,
          loanCategorys: _.map(
            _.filter(equipmentAssets, (item) => Boolean(item.category)),
            (item) => ({
              categoryId: item.category,
              categoryName: item.categoryName,
              assetCount: item.loanQty,
              // assetIds: _.map(item.assets, (assetItem) => assetItem.assetId),
            })
          ),
          patientNo: formData.patient,
          provideWardId: provideWardId,
          categorySatelliteStoresId,
          remark: formData.remarks,
          wardId: wardId,
          adsWardLocationId,
          provideWardName: providersOptions.find((item) => item.value === formData.provider)?.provideWardName ?? "",
          wardName: loginInfo.wardName,
          model: 1,
          adsCategoryId,
          currentWard: loginInfo.wardId,
          instCode: userInfo.hospCode,
          isAdsServer,
          loanType: 'BORROW',
          loanUniqueKey,
        };

        await equipmentAPI.newLoanRequset(params);
        submitLock = false;
        dialogFinishHandler();
      } catch (error) {
        submitLock = false;
        if (error.code === 'ECONNABORTED') {
          dialogFinishHandler();
        }
        console.log(error);
      }
    },
    [formData, equipmentAssets, dialogFinishHandler, providersOptions, loginInfo.wardId, loanUniqueKey, provideWardId]
  );

  // 提交表單事件
  const dialogOpenHandler = useCallback(() => {
    try {
      setAdsPopup(true);
      adsRef?.current?.getAdsLocation?.();
    } catch (error) {
      console.log(error);
    }
  }, []);

  // 根據 provider 限制可以選擇的 category
  const provideCategorys = useMemo(() => {
    let categorys = [];

    if (formData.provider) {
      // 過濾當前 provid 可選的所有 category
      categorys = _.map(
        _.find(providersOptions, (item) => item.value === formData.provider)?.categorys ?? [],
        (item) => item.id
      );
    } else {
      // 過濾當前所有 provider 可選的所有 category
      _.forEach(providersOptions, (providerItem) => {
        _.forEach(providerItem?.categorys ?? [], (item) => {
          if (!_.some(categorys, (categoryItem) => categoryItem === item.id)) {
            categorys.push(item.id);
          }
        });
      });
    }

    return categorys;
  }, [providersOptions, formData.provider]);

  // Scan Equipment 的組件和調用函數,返回第一個是組件，第二個是開啟的函數
  const [ScanPortalHN, openHNScanPage] = useScanPage(
    handleScanPatientQrCode(formChangeHandler('patient'), formChangeHandler('bedID'))
  );
  const [ScanPortalRemarks, openScanPageRemarks] = useScanPage(formChangeHandler('remarks', true));

  // provider no option 时，点击autocomplete时的事件
  const providerOnOpen = useCallback(async () => {
    try {
      if (providersOptions.length === 0) {
        await getProvider();
      }
    } catch (error) {
      console.log(error);
    }
  }, [getProvider, providersOptions]);

  // request ward no option时，点击autocomplete时的事件
  const wardOnOpen = useCallback(async () => {
    try {
      if (wardsOptions.length === 0) {
        await getWardList();
      }
    } catch (error) {
      console.log(error);
    }
  }, [getWardList, wardsOptions]);

  // confirm button disable
  const confirmDisable = useMemo(
    () =>
      !(formData.provider && formData.ward) ||
      _.some(equipmentAssets, (item) => item.categoryName === '') ||
      adsPopup ||
      (requiredPatientHN && !formData.patient),
    [formData.provider, formData.ward, errors.patient, equipmentAssets, adsPopup, requiredPatientHN, formData.patient]
  );

  const getBedNo = useCallback(async () => {
    try {
      if (formData.patient.toUpperCase().startsWith('WB ')) {
        handleScanHkid(formChangeHandler('patient'), formChangeHandler('bedID'), formData.patient);
      } else {
        const { result } = await equipmentAPI.getBedNo({
          patientNo: formData.patient.replace(/\s*/g, ''),
        });
        formChangeHandler('bedID')(result.BedNo);
      }
    } catch (error) {
      console.log(error);
    }
  }, [formData.patient, formChangeHandler]);

  const adsWardInfo = useMemo(() => {
    let provider = '';
    let request = '';

    if (formData.ward) {
      request = _.find(wardsOptions, (item) => item.value === formData.ward)?.text ?? '';
    }

    if (formData.provider) {
      provider = _.find(providersOptions, (item) => item.value === formData.provider)?.text ?? '';
    }

    return {
      provider,
      request,
    };
  }, [formData.ward, formData.provider, wardsOptions, providersOptions]);
  const { remarkList: activeRemarkList, getRemarksListAPI } = useGetRemarksListAPI();

  // 选择remark
  const handleSelectRemark = (remarks) => {
    setFormData((o = {}) => ({
      ...o,
      remarks: `${o?.remarks || ''} ${remarks}`.slice(0, 99),
    }));
  };

  useEffect(() => {
    if (equipmentAssets?.[flag]?.category) {
      getRemarksListAPI({ hospitalCategoryIds: [equipmentAssets?.[flag]?.category] });
    }
  }, [equipmentAssets?.[flag]?.category]);

  return (
    <>
      <ScanPortalHN />
      <ScanPortalRemarks />
      <DiaLog
        open={true}
        handleClose={dialogCloseHandler}
        handleCancel={dialogCloseHandler}
        handleConfirm={dialogOpenHandler}
        title={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_TITLE_B')}
        cancel={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_CANCEL')}
        confirm={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_CONFIRM')}
        dialogfootStyle={{ marginTop: '1rem' }}
        disabled={confirmDisable}
      >
        <Box display="flex" flexDirection="column" pb="1.25rem">
          <EquipmentAsset
            data={equipmentAssets}
            flag={flag}
            setFlag={setFlag}
            changeDataHandler={equipmentAssetsHandler}
            provider={provideWardId}
            isFloorProvider={currentProvider?.isFloor ?? false}
            provideCategorys={provideCategorys}
            controller={{
              assetId: true,
              category: true,
              loanQty: true,
              asset: true,
              Decline: true,
            }}
            modelB={true}
            newRequest={true}
            setSelectedCategoryWards={setSelectedCategoryWards}
            categorySatelliteStoresId={categorySatelliteStoresId}
          />
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" pb="1.313rem">
          <Box>
            <div className={classes.title}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REQUEST_WARD')} <span className={classes.star}>*</span>
            </div>
            <AutoCompleteInput
              className={classes.wardAutoSelect}
              id="ViewRequest__autoSelect-ward"
              width="19.375rem"
              height="3.5rem"
              itemList={wardsOptions}
              placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REQUEST_WARD_PLACEHOLDER')}
              value={formData.ward}
              onChange={formChangeHandler('ward')}
              onOpen={wardOnOpen}
            />
          </Box>
          <Box>
            <div className={classes.title}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_PROVIDER')} <span className={classes.star}>*</span>
            </div>
            <AutoCompleteInput
              className={classes.wardAutoSelect}
              id="ViewRequest__autoSelect-provider"
              width="19.375rem"
              height="3.5rem"
              itemList={providersOptions}
              placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_PROVIDER_PLACEHOLDER')}
              value={formData.provider}
              onChange={formChangeHandler('provider')}
              onOpen={providerOnOpen}
              renderGroup={(params) => renderProviderGroup(params, equipmentAssets, flag, providersOptions)}
              whiteSpace
            />
          </Box>
        </Box>
        <Box
          id="equipment-button-otherinformation"
          className={classes.otherInfoBtn}
          display="flex"
          p="1.125rem 0.813rem"
          borderTop="solid 1px #e6e7e8"
          borderBottom="solid 1px #e6e7e8"
          onClick={otherInformationController}
        >
          <div className={classes.dropDownButton}>
            {otherInformationOpen ? (
              <DropUpIcon className={classes.dropDownIcon} />
            ) : (
              <DropDownIcon className={classes.dropDownIcon} />
            )}
          </div>
          <div className={classes.otherInformationTitle}>
            {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_OTHER_INFORMATION')}
          </div>
        </Box>
        {otherInformationOpen && (
          <Box display="flex" flexDirection="column" p="1rem 0 1.5rem" borderBottom="solid 1px #e6e7e8">
            <Box display="flex" justifyContent="space-between" mb="1.25rem">
              <TitleInput
                id="equipment-input-patient"
                title={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_PATIENT_HN')}
                placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_PATIENT_HN_PLACEHOLDER')}
                flexBasis="48%"
                endAdornment={
                  isPad ? (
                    <ButtonBase
                      id="equipment-button-patient"
                      onClick={() => {
                        (async () => {
                          await openHNScanPage();
                        })();
                      }}
                    >
                      <ScanIcon className={classes.scanIcon} />
                    </ButtonBase>
                  ) : (
                    <div />
                  )
                }
                value={formData.patient}
                onChange={formChangeHandler('patient')}
                length={EQUIPMENT_LENGTH.REQUEST_HN}
                enterHandler={getBedNo}
                onFocus={barcodeControl(false)}
                onBlur={() => {
                  barcodeControl(true)();
                  setFormData((preData) => ({ ...preData, patient: preData.patient.replace(/\s*/g, '') }));
                }}
                error={Boolean(errors.patient)}
                errorMsg={errors.patient}
                required={requiredPatientHN}
              />
              <TitleInput
                id="equipment-input-bed"
                title={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_BED_ID')}
                placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_BED_ID_PLACEHOLDER')}
                flexBasis="48%"
                value={formData.bedID}
                onChange={formChangeHandler('bedID')}
                length={EQUIPMENT_LENGTH.REQUEST_BED}
              />
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection="column"
              // alignItems="flex-end"
              mb="1.313rem"
            >
              <Box display="flex" justifyContent="space-between">
                <div className={classes.title}>{i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REMARK')}</div>
                <AddRemarks list={activeRemarkList} handleSelect={handleSelectRemark} />
              </Box>
              <Box display="flex">
                <TextField
                  id="ViewRequest-input-remark"
                  className={classes.textField}
                  placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REMARK_PLACEHOLDER')}
                  multiline={true}
                  fullWidth={true}
                  rows={4}
                  value={formData.remarks}
                  onChange={formChangeHandler('remarks')}
                  style={{ marginTop: '1rem' }}
                  inputRef={inputRef}
                  onFocus={() => setBarcodeOpen(false)}
                  onBlur={() => setBarcodeOpen(true)}
                />
                {isPad && (
                  <StyleButton
                    id="ViewRequest-button-scan-remark"
                    className={classes.scanButton}
                    width="14.125rem"
                    text={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_SCAN_EQUIPMENT')}
                    Icon={ScanIcon}
                    iconStyle={{ fontSize: '1.5rem', marginRight: '0.75rem' }}
                    onClick={openScanPageRemarks}
                    style={{ marginTop: '1rem' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DiaLog>
      <SingleADSDialog
        handleNewRequest={newLoanRequestAPI}
        equipmentAssets={equipmentAssets}
        formData={formData}
        handleClose={() => setAdsPopup(false)}
        adsType="new"
        adsWardInfo={adsWardInfo}
        adsPopup={adsPopup}
        ref={adsRef}
        provideWardId={provideWardId}
        requestWard={wardId}
        adsWardLocationId={adsWardLocationId}
        categorySatelliteStoresId={categorySatelliteStoresId}
      />
    </>
  );
};

export default NewLoanRequestModelBDialog;
